/* Keep a collection page's image memory FLAT instead of merely slower-growing.

   Background: the collection grid uses infinite scroll and never unloads. Every
   product image a shopper scrolls past decodes and stays decoded - measured on
   an iPhone 12 profile, /collections/all retained 338 MB after ~310 products,
   and scrolling back to the top released none of it. That is what was killing
   Safari tabs mid-scroll.

   Deferring the extra slide images (card-product-var.liquid + the Swiper lazy
   config in pm-custom.js) halved the cost, to ~160 MB at the same depth. This
   closes the rest of the gap: it is the growth itself that is the problem, not
   the slope. Images belonging to cards far outside the viewport have their
   `src`/`srcset` stashed on the node and cleared, which lets the browser drop
   the decoded bitmap; they are restored before the card comes back into view.

   Deliberately conservative:
   - A card must be well outside the viewport before anything is unloaded, so
     normal reading and short scroll-backs never hit it.
   - The `<img>` keeps its width/height attributes and its CSS box, so nothing
     reflows and no layout shift is introduced by clearing the source.
   - Restore happens on approach, not on entry, so an image is back before it
     can be seen.
   - If IntersectionObserver is missing, the script does nothing at all and the
     page behaves exactly as it does today. */
(function () {
  if (!('IntersectionObserver' in window)) return;

  /* Both product grids that grow without bound as the shopper scrolls: the
     collection grid, and the published-date grid on its own page (which pages
     in more cards from assets/new-arrivals.js). A grid that is fully rendered
     up front does not need this and is not listed. */
  var GRIDS = ['#product-grid', '.new-arrivals__grid'];
  var CARD = GRIDS.map(function (sel) { return sel + ' > li'; }).join(', ');

  /* 1x1 transparent GIF. Swapped in rather than removing the attribute so the
     element never renders a broken-image glyph mid-scroll. */
  var BLANK = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

  /* How far outside the viewport a card may sit before its images are dropped.
     300% either side is roughly three screens of scrolling in each direction -
     generous enough that a shopper scrolling back up sees no reload, while
     still capping how many images are ever decoded at once. */
  var KEEP_MARGIN = '300% 0px';

  function unload(card) {
    if (card.dataset.imgRecycled === 'true') return;
    var imgs = card.querySelectorAll('img');
    var touched = 0;
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      /* Never touch an image that has not resolved a source yet: that is
         either a Swiper-lazy slide the shopper has not reached, or an image
         still in flight. Clearing those would lose nothing and risk a race. */
      if (!img.currentSrc && !img.getAttribute('src')) continue;
      if (img.dataset.recycleSrc) continue;

      img.dataset.recycleSrc = img.getAttribute('src') || '';
      var srcset = img.getAttribute('srcset');
      if (srcset) img.dataset.recycleSrcset = srcset;

      img.removeAttribute('srcset');
      img.setAttribute('src', BLANK);
      touched++;
    }
    if (touched) card.dataset.imgRecycled = 'true';
  }

  function restore(card) {
    if (card.dataset.imgRecycled !== 'true') return;
    var imgs = card.querySelectorAll('img');
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (!img.dataset.recycleSrc && !img.dataset.recycleSrcset) continue;
      if (img.dataset.recycleSrcset) {
        img.setAttribute('srcset', img.dataset.recycleSrcset);
        delete img.dataset.recycleSrcset;
      }
      if (img.dataset.recycleSrc) img.setAttribute('src', img.dataset.recycleSrc);
      delete img.dataset.recycleSrc;
    }
    delete card.dataset.imgRecycled;
  }

  var observer = new IntersectionObserver(function (entries) {
    for (var i = 0; i < entries.length; i++) {
      if (entries[i].isIntersecting) restore(entries[i].target);
      else unload(entries[i].target);
    }
  }, { rootMargin: KEEP_MARGIN });

  var watched = new WeakSet();

  function scan() {
    var cards = document.querySelectorAll(CARD);
    for (var i = 0; i < cards.length; i++) {
      if (watched.has(cards[i])) continue;
      watched.add(cards[i]);
      observer.observe(cards[i]);
    }
  }

  function init() {
    var grids = [];
    for (var g = 0; g < GRIDS.length; g++) {
      var found = document.querySelectorAll(GRIDS[g]);
      for (var j = 0; j < found.length; j++) grids.push(found[j]);
    }
    if (!grids.length) return;
    scan();
    /* Infinite scroll appends cards, and filtering replaces the whole grid, so
       new cards have to be picked up as they arrive. Debounced: an append adds
       a dozen at once. */
    var timer = null;
    var watcher = new MutationObserver(function () {
      window.clearTimeout(timer);
      timer = window.setTimeout(scan, 200);
    });
    for (var k = 0; k < grids.length; k++) {
      watcher.observe(grids[k], { childList: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
